import { yupResolver } from '@hookform/resolvers/yup'
import { endOfDay, startOfDay } from 'date-fns'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useLazyGetArisArisIngestionProcessConfigQuery } from '../../app/apiSlice'
import { Button, InputCalendar, InputField, Text, Toggle } from '../../common/designs'
import ExportHistoryPanel from './ExportHistoryPanel'
import useArisIngestionTransfer from './useArisIngestionTransfer'
import { Inputs, formSchema } from './utils'

interface Props {
  processId: string
  isPopupOpen: boolean
}

const ArisDataIngestionForm = ({ processId, isPopupOpen }: Props) => {
  const {
    showTimeRange,
    setShowTimeRange,
    isTransferStarted,
    isPollingComplete,
    isTimeout,
    hasFirstPollingResult,
    logData,
    logError,
    isFetching,
    isStartingTransfer,
    isSavingConfigurations,
    onStartTransfer,
    onSaveConfigurations,
  } = useArisIngestionTransfer(processId)

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm<Inputs>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      aris_dataset: '',
      aris_table: '',
      time_range_start: null,
      time_range_end: null,
      should_trigger_load: false,
      is_incremental_table: false,
      is_auto_sync: false,
    },
  })

  const [loadArisIngestionProcessConfig, { data: arisIngestionProcessConfig }] =
    useLazyGetArisArisIngestionProcessConfigQuery()

  useEffect(() => {
    if (isPopupOpen && processId) {
      loadArisIngestionProcessConfig({ processId })
    }
  }, [isPopupOpen, processId, loadArisIngestionProcessConfig])

  useEffect(() => {
    if (arisIngestionProcessConfig) {
      const {
        aris_dataset,
        aris_table,
        time_range_start,
        time_range_end,
        should_trigger_load,
        is_incremental_table,
        is_auto_sync,
      } = arisIngestionProcessConfig
      reset({
        aris_dataset: aris_dataset || '',
        aris_table: aris_table || '',
        time_range_start: time_range_start ? new Date(time_range_start.replace('Z', '')) : null,
        time_range_end: time_range_end ? new Date(time_range_end.replace('Z', '')) : null,
        should_trigger_load: should_trigger_load || false,
        is_incremental_table: is_incremental_table || false,
        is_auto_sync: is_auto_sync || false,
      })
      setShowTimeRange(Boolean(time_range_start || time_range_end))
    }
  }, [arisIngestionProcessConfig, reset, setShowTimeRange])

  return (
    <div>
      <Text size='l' weight={600} className='mt-6'>
        ARIS Data Ingestion API
      </Text>
      <div className='rounded-lg border border-gray-300 bg-white p-6'>
        <div className='flex flex-col gap-6'>
          <form
            onSubmit={handleSubmit(onStartTransfer)}
            className='grid grid-cols-1 gap-4 md:grid-cols-2'
          >
            <div>
              <Controller
                name='aris_dataset'
                control={control}
                render={({ field }) => (
                  <InputField
                    label='ARIS Dataset'
                    type='text'
                    error={errors.aris_dataset?.message}
                    className='w-full'
                    placeholder='MyARISDataset'
                    {...field}
                  />
                )}
              />
            </div>

            <div>
              <Controller
                name='aris_table'
                control={control}
                render={({ field }) => (
                  <InputField
                    label='ARIS Table'
                    type='text'
                    error={errors.aris_table?.message}
                    className='w-full'
                    placeholder='MyARISTable'
                    {...field}
                  />
                )}
              />
            </div>


            <div>
              <Controller
                name='should_trigger_load'
                control={control}
                render={({ field }) => (
                  <Toggle label='Trigger Load' value={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            <div>
              <Controller
                name='is_incremental_table'
                control={control}
                render={({ field }) => (
                  <Toggle label='Incremental Table' value={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            <div>
              <Controller
                name='is_auto_sync'
                control={control}
                render={({ field }) => (
                  <Toggle label='Automatic Synchronization' value={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            <div></div>

            <div>
              <Toggle
                label='Filter by Time Range'
                value={showTimeRange}
                onChange={(value) => {
                  setShowTimeRange(value)
                  if (!value) {
                    setValue('time_range_start', null)
                    setValue('time_range_end', null)
                  }
                }}
              />
            </div>

            {showTimeRange && (
              <div className='flex flex-col gap-2 md:col-span-2'>
                <Text size='s' weight={500} className='text-gray-700'>
                  Time Range
                </Text>
                <div className='flex flex-col gap-2 md:flex-row'>
                  <Controller
                    name='time_range_start'
                    control={control}
                    render={({ field }) => (
                      <div className='flex flex-col gap-1'>
                        <Text size='xs' className='text-gray-600'>
                          Start Date
                        </Text>
                        <InputCalendar
                          minDate={new Date('2020-01-01')}
                          maxDate={new Date()}
                          selected={field.value}
                          onChange={(date) => field.onChange(date ? startOfDay(date) : null)}
                          placeholderText='Select start date'
                        />
                        {errors.time_range_start && (
                          <Text size='xs' className='text-red-600'>
                            {errors.time_range_start.message}
                          </Text>
                        )}
                      </div>
                    )}
                  />

                  <Controller
                    name='time_range_end'
                    control={control}
                    render={({ field }) => (
                      <div className='flex flex-col gap-1'>
                        <Text size='xs' className='text-gray-600'>
                          End Date
                        </Text>
                        <InputCalendar
                          minDate={new Date('2020-01-01')}
                          maxDate={new Date()}
                          selected={field.value}
                          onChange={(date) => field.onChange(date ? endOfDay(date) : null)}
                          placeholderText='Select end date'
                        />
                        {errors.time_range_end && (
                          <Text size='xs' className='text-red-600'>
                            {errors.time_range_end.message}
                          </Text>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            )}

            <div className='flex flex-col gap-3 pt-2 md:col-span-2 md:flex-row md:justify-end'>
              {isSavingConfigurations ? (
                <Text size='s' className='text-gray-500 md:w-56 md:self-center md:text-right'>
                  Saving...
                </Text>
              ) : (
                <Button
                  type='button'
                  variant='secondary'
                  className='w-full md:w-56'
                  onClick={handleSubmit(onSaveConfigurations)}
                >
                  Save Configurations
                </Button>
              )}
              <Button type='submit' className='w-full md:w-56' disabled={isStartingTransfer}>
                {isStartingTransfer ? 'Starting Transfer...' : 'Start Direct Transfer'}
              </Button>
            </div>

            {isFetching && !isTimeout && (
              <Text size='xs' className='text-blue-600 md:col-span-2'>
                🔄 Verifying status...
              </Text>
            )}
            {isPollingComplete && !isTimeout && (
              <Text size='xs' className='text-green-600 md:col-span-2'>
                ✅ Completed
              </Text>
            )}
            {isTimeout && (
              <Text size='xs' className='text-orange-600 md:col-span-2'>
                ⏱️ Timeout: The operation exceeded 5 minutes without completion. Please check the
                status manually.
              </Text>
            )}

            {Boolean(logError) && (
              <Text size='xs' className='text-red-600 md:col-span-2'>
                ❌ Error: {(logError as any).data?.message}
              </Text>
            )}
          </form>
        </div>
      </div>

      <Text size='l' weight={600} className='mt-6'>
        Data Export History
      </Text>
      <div className='rounded-lg border border-gray-300 bg-white p-6'>
        <div className='flex flex-col gap-6'>
          <ExportHistoryPanel
            isTransferStarted={isTransferStarted}
            hasFirstPollingResult={hasFirstPollingResult}
            events={logData?.events ?? []}
          />
        </div>
      </div>
    </div>
  )
}

export default ArisDataIngestionForm

